// /bookings — app-style, mobile-first list of booked calls.
// Day-grouped responsive cards (no horizontal-scroll table), with search,
// filters, pagination, and per-card book / reschedule / status / delete actions.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FaPhoneAlt, FaSpinner, FaSearch, FaPlus, FaEdit, FaTrash, FaChevronRight, FaCalendarDay,
} from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import Pagination from '../../../components/shared/Pagination';
import formatPhone from '../../../components/shared/formatPhone';
import StatusBadge from '../components/StatusBadge';
import BookingModal from '../components/BookingModal';
import { listCalls, setCallStatus, deleteCall } from '../callScheduleService';
import {
  formatEastern, formatEasternTime, relativeDayLabel, isToday, isTomorrow, STATUS_OPTIONS,
} from '../utils/scheduleUtils';

/* ------------------------------------ styles ------------------------------------ */

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: ${p => p.theme.spacing(2.5)};
  .titles { flex: 1; min-width: 0; }
  h1 { color: ${p => p.theme.colors.primary}; margin: 0; font-size: 1.45rem; display: flex; align-items: center; gap: 10px; }
  .count { color: ${p => p.theme.colors.textSecondary}; font-size: 0.82rem; margin-top: 2px; }
`;

const BookBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0;
  padding: 11px 18px; border: none; border-radius: 999px;
  background: ${p => p.theme.colors.primary}; color: #fff; font-weight: 700; font-size: 0.9rem;
  cursor: pointer; box-shadow: 0 4px 14px ${p => p.theme.colors.primary}55;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px ${p => p.theme.colors.primary}66; }
  &:active { transform: translateY(0); }
  .lbl { @media (max-width: 480px) { display: none; } }
`;

const SearchBox = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: ${p => p.theme.spacing(2)};
  background: ${p => p.theme.colors.surface};
  border: 1.5px solid ${p => p.theme.colors.inputGray}33;
  border-radius: 999px; padding: 11px 16px;
  transition: border-color .15s ease;
  &:focus-within { border-color: ${p => p.theme.colors.primary}; }
  svg { color: ${p => p.theme.colors.textSecondary}; flex-shrink: 0; }
  input { flex: 1; background: transparent; border: none; outline: none; color: ${p => p.theme.colors.textPrimary}; font-size: 0.95rem; }
`;

const Filters = styled.div`
  display: flex; gap: 8px; margin-bottom: ${p => p.theme.spacing(2.5)};
  overflow-x: auto; padding-bottom: 4px;
  -ms-overflow-style: none; scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Pill = styled.button`
  flex-shrink: 0; padding: 8px 16px; border-radius: 999px; white-space: nowrap;
  border: 1.5px solid ${p => p.$active ? p.theme.colors.primary : p.theme.colors.inputGray + '33'};
  background: ${p => p.$active ? p.theme.colors.primary : 'transparent'};
  color: ${p => p.$active ? '#fff' : p.theme.colors.textSecondary};
  font-size: 0.84rem; font-weight: 600; cursor: pointer; transition: all .15s ease;
  &:hover { border-color: ${p => p.theme.colors.primary}; }
`;

const GroupLabel = styled.div`
  display: flex; align-items: center; gap: 8px;
  margin: ${p => p.theme.spacing(2.5)} 0 ${p => p.theme.spacing(1.5)};
  font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px;
  color: ${p => p.$soon ? p.theme.colors.primary : p.theme.colors.textSecondary};
  svg { font-size: 0.8rem; }
  &::after { content: ''; flex: 1; height: 1px; background: ${p => p.theme.colors.inputGray}22; }
`;

const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${p => p.theme.spacing(2)};
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 16px;
  border: 1px solid ${p => p.theme.colors.inputGray}22;
  border-left: 4px solid ${p => p.$accent};
  padding: 16px 16px 12px;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(0,0,0,0.28); }
`;

const CardTop = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px;
`;

const TimeChip = styled.div`
  display: inline-flex; align-items: baseline; gap: 5px;
  padding: 5px 12px; border-radius: 10px; font-weight: 800; font-size: 1rem;
  background: ${p => p.$color}1f; color: ${p => p.$color};
`;

const Main = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 14px;
`;

const Avatar = styled.div`
  width: 42px; height: 42px; flex-shrink: 0; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, #e07d00);
  color: #fff; font-weight: 700; font-size: 0.92rem;
`;

const Who = styled.div`
  flex: 1; min-width: 0;
  .name { color: ${p => p.theme.colors.textPrimary}; font-weight: 600; font-size: 1rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sub { color: ${p => p.theme.colors.textSecondary}; font-size: 0.82rem; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const Chevron = styled(FaChevronRight)`
  color: ${p => p.theme.colors.inputGray}; flex-shrink: 0;
`;

const Actions = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding-top: 12px; border-top: 1px solid ${p => p.theme.colors.inputGray}1f;
`;

const ActBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  min-height: 38px; padding: 0 12px; border-radius: 10px;
  border: 1px solid ${p => (p.$danger ? p.theme.colors.error : p.theme.colors.inputGray) + '44'};
  background: transparent; cursor: pointer; font-size: 0.82rem; font-weight: 600;
  color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.textSecondary};
  transition: all .15s ease;
  &:hover { border-color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary}; color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary}; }
`;

const StatusSelect = styled.select`
  flex: 1; min-height: 38px; padding: 0 10px; border-radius: 10px; cursor: pointer;
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputGray}44; font-size: 0.82rem; font-weight: 600;
`;

const Loader = styled.div`
  display: flex; justify-content: center; padding: 90px; color: ${p => p.theme.colors.primary};
  svg { font-size: 32px; animation: spin 1s linear infinite; }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const Empty = styled.div`
  text-align: center; padding: 70px 20px; color: ${p => p.theme.colors.textSecondary};
  .ico { font-size: 2.4rem; color: ${p => p.theme.colors.inputGray}; margin-bottom: 14px; }
  .t { font-size: 1.05rem; color: ${p => p.theme.colors.textPrimary}; font-weight: 600; margin-bottom: 6px; }
  .s { font-size: 0.88rem; }
`;

/* ----------------------------------- data cfg ----------------------------------- */

const FILTERS = [
  { key: 'upcoming', label: 'Upcoming', build: () => ({ status: 'scheduled', from: new Date().toISOString(), sort: 'slot_start', order: 'asc' }) },
  { key: 'past', label: 'Past', build: () => ({ to: new Date().toISOString(), sort: 'slot_start', order: 'desc' }) },
  { key: 'completed', label: 'Completed', build: () => ({ status: 'completed', sort: 'slot_start', order: 'desc' }) },
  { key: 'no_show', label: 'No-show', build: () => ({ status: 'no_show', sort: 'slot_start', order: 'desc' }) },
  { key: 'canceled', label: 'Canceled', build: () => ({ status: 'canceled', sort: 'slot_start', order: 'desc' }) },
  { key: 'all', label: 'All', build: () => ({ sort: 'slot_start', order: 'desc' }) },
];

const PER_PAGE_OPTIONS = [25, 50, 100];

const initials = (name) =>
  (name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const accentFor = (iso) => (isToday(iso) ? '#F69620' : isTomorrow(iso) ? '#84C959' : '#787878');

/* ----------------------------------- component ---------------------------------- */

const BookingsPage = () => {
  const navigate = useNavigate();
  const [filterKey, setFilterKey] = useState('upcoming');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });

  const [bookingModal, setBookingModal] = useState(null); // { mode, booking }
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const filter = FILTERS.find(f => f.key === filterKey) || FILTERS[0];
      const params = {
        ...filter.build(),
        search: search || undefined,
        limit: perPage,
        offset: (page - 1) * perPage,
      };
      const res = await listCalls(params);
      setCalls(res.calls || []);
      setTotal(res.total || 0);
    } catch (err) {
      setFeedback({ show: true, message: 'Failed to load bookings', success: false });
    } finally {
      setLoading(false);
    }
  }, [filterKey, search, page, perPage]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const notify = (message, success = true) => setFeedback({ show: true, message, success });

  const changeStatus = async (call, status) => {
    if (status === call.status) return;
    try {
      await setCallStatus(call.id, status);
      notify('Status updated');
      fetchCalls();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to update status', false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteCall(deleteTarget.id);
      setDeleteTarget(null);
      notify('Booking deleted');
      fetchCalls();
    } catch (err) {
      setDeleteTarget(null);
      notify(err.response?.data?.message || 'Failed to delete', false);
    }
  };

  const onSaved = (message) => { notify(message); fetchCalls(); };
  const selectFilter = (key) => { setFilterKey(key); setPage(1); };

  // Group the current page's calls by Eastern day for an app-like sectioned list.
  const groups = useMemo(() => {
    const map = new Map();
    for (const c of calls) {
      const label = relativeDayLabel(c.slot_start);
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(c);
    }
    return [...map.entries()];
  }, [calls]);

  const filterLabel = FILTERS.find(f => f.key === filterKey)?.label.toLowerCase() || '';

  const stop = (e) => e.stopPropagation();

  return (
    <DashboardLayout>
      <Page>
        <Header>
          <div className="titles">
            <h1><FaPhoneAlt /> Call Scheduling</h1>
            {!loading && <div className="count">{total} {total === 1 ? 'booking' : 'bookings'}</div>}
          </div>
          <BookBtn onClick={() => setBookingModal({ mode: 'create', booking: null })}>
            <FaPlus /> <span className="lbl">Book a Call</span>
          </BookBtn>
        </Header>

        <SearchBox>
          <FaSearch />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, or phone…"
          />
        </SearchBox>

        <Filters>
          {FILTERS.map(f => (
            <Pill key={f.key} $active={filterKey === f.key} onClick={() => selectFilter(f.key)}>
              {f.label}
            </Pill>
          ))}
        </Filters>

        {loading ? (
          <Loader><FaSpinner /></Loader>
        ) : calls.length === 0 ? (
          <Empty>
            <div className="ico"><FaCalendarDay /></div>
            <div className="t">No {filterLabel} bookings</div>
            <div className="s">{search ? 'Try a different search.' : 'Booked calls will show up here.'}</div>
          </Empty>
        ) : (
          <>
            {groups.map(([label, items]) => {
              const soon = label === 'Today' || label === 'Tomorrow';
              return (
                <div key={label}>
                  <GroupLabel $soon={soon}>
                    {soon && <FaCalendarDay />} {label} · {items.length}
                  </GroupLabel>
                  <Grid>
                    {items.map(call => {
                      const accent = accentFor(call.slot_start);
                      const name = call.name || call.lead?.name || 'Unknown';
                      const phone = call.phone || call.lead?.phone;
                      return (
                        <Card key={call.id} $accent={accent} onClick={() => navigate(`/bookings/${call.id}`)}>
                          <CardTop>
                            <TimeChip $color={accent}>{formatEasternTime(call.slot_start)}</TimeChip>
                            <StatusBadge status={call.status} />
                          </CardTop>

                          <Main>
                            <Avatar>{initials(name)}</Avatar>
                            <Who>
                              <div className="name">{name}</div>
                              <div className="sub">
                                {phone ? formatPhone(phone) : 'No phone'}
                                {call.source === 'public' ? ' · via website' : ' · added by team'}
                              </div>
                            </Who>
                            <Chevron />
                          </Main>

                          <Actions onClick={stop}>
                            <ActBtn onClick={() => setBookingModal({ mode: 'edit', booking: call })}>
                              <FaEdit /> Reschedule
                            </ActBtn>
                            <StatusSelect
                              value={call.status}
                              onChange={(e) => changeStatus(call, e.target.value)}
                              title="Change status"
                            >
                              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </StatusSelect>
                            <ActBtn $danger onClick={() => setDeleteTarget(call)} title="Delete">
                              <FaTrash />
                            </ActBtn>
                          </Actions>
                        </Card>
                      );
                    })}
                  </Grid>
                </div>
              );
            })}

            <Pagination
              totalItems={total}
              itemsPerPage={perPage}
              currentPage={page}
              paginate={setPage}
              itemsPerPageOptions={PER_PAGE_OPTIONS}
              onItemsPerPageChange={(n) => { setPerPage(n); setPage(1); }}
            />
          </>
        )}
      </Page>

      <BookingModal
        isOpen={!!bookingModal}
        mode={bookingModal?.mode || 'create'}
        booking={bookingModal?.booking || null}
        onClose={() => setBookingModal(null)}
        onSaved={onSaved}
      />

      <Modal
        title="Delete Booking"
        isOpen={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      >
        <p style={{ color: '#ccc', lineHeight: 1.6 }}>
          Permanently delete the booking for{' '}
          <strong style={{ color: '#fff' }}>{deleteTarget?.name || deleteTarget?.lead?.name || deleteTarget?.phone}</strong>
          {deleteTarget && <> on <strong style={{ color: '#fff' }}>{formatEastern(deleteTarget.slot_start)}</strong></>}? This cannot be undone.
        </p>
      </Modal>

      <ModalNotification
        isOpen={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DashboardLayout>
  );
};

export default BookingsPage;
